import { useState } from 'react';
import {
  usedForSpending,
  type IncomeSchedule,
  type IncomeSourceInput,
  type LocalDate,
  type WeekendRule,
} from '@tahi/core';
import { Button } from '../../../ui';
import { Segmented } from '../../../ui/Segmented';
import { AccountChips } from './AccountChips';
import { ChoiceChips } from './ChoiceChips';
import { TextField } from '../../../ui/TextField';
import { copy } from '../copy';
import { dayOfMonthLabel, minorToInput, parsePesoInput } from '../format';
import { INPUT } from '../styles';

type ScheduleKind = IncomeSchedule['kind'];

const SALARY_KINDS: { value: ScheduleKind; label: string }[] = [
  { value: 'semi_monthly', label: copy.twiceAMonth },
  { value: 'monthly', label: copy.monthly },
  { value: 'biweekly', label: copy.everyTwoWeeks },
  { value: 'weekly', label: copy.weekly },
];
const BONUS_KINDS: { value: ScheduleKind; label: string }[] = [
  { value: 'yearly_dates', label: copy.sameDatesEveryYear },
  { value: 'every_n_months', label: copy.everyFewMonths },
];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface YearlyRow {
  month: number;
  day: string;
  amount: string;
}

export interface IncomeSourceFormProps {
  kind: 'salary' | 'bonus';
  today: LocalDate;
  initial?: IncomeSourceInput;
  error?: string | null;
  onSubmit(input: IncomeSourceInput): void | Promise<void>;
  /** v0.5: accounts for "Receive into" (left out: the field isn't shown). */
  accounts?: { id: string; name: string; useForSpending?: boolean }[];
  /** v0.5: salary sources a bonus can arrive with. */
  salaries?: { id: string; name: string; schedule: IncomeSchedule }[];
}

const pad = (n: number) => String(n).padStart(2, '0');
const toInt = (s: string) => (/^\d{1,2}$/.test(s.trim()) ? Number(s.trim()) : Number.NaN);

function initialRows(initial?: IncomeSourceInput): YearlyRow[] {
  if (initial?.schedule.kind === 'yearly_dates') {
    return initial.schedule.dates.map((md, i) => ({
      month: Number(md.slice(0, 2)),
      day: String(Number(md.slice(3, 5))),
      amount: minorToInput(initial.defaultAmounts[i] ?? 0),
    }));
  }
  return [{ month: 12, day: '15', amount: '' }];
}

export function IncomeSourceForm({
  kind,
  today,
  initial,
  error = null,
  onSubmit,
  accounts,
  salaries = [],
}: IncomeSourceFormProps) {
  const s0 = initial?.schedule;
  const [name, setName] = useState(initial?.name ?? '');
  const [schedKind, setSchedKind] = useState<ScheduleKind>(
    s0?.kind ?? (kind === 'salary' ? 'semi_monthly' : 'yearly_dates'),
  );
  const [day1, setDay1] = useState(s0?.kind === 'semi_monthly' ? String(s0.days[0]) : '15');
  const [day2, setDay2] = useState(s0?.kind === 'semi_monthly' ? String(s0.days[1]) : '31');
  const [monthDay, setMonthDay] = useState(s0?.kind === 'monthly' ? String(s0.day) : '30');
  const [anchor, setAnchor] = useState<LocalDate>(s0 && 'anchor' in s0 ? s0.anchor : today);
  const [everyN, setEveryN] = useState(s0?.kind === 'every_n_months' ? String(s0.n) : '3');
  const [amount1, setAmount1] = useState(
    initial && s0?.kind !== 'yearly_dates' ? minorToInput(initial.defaultAmounts[0] ?? 0) : '',
  );
  const [amount2, setAmount2] = useState(
    initial && s0?.kind === 'semi_monthly' ? minorToInput(initial.defaultAmounts[1] ?? 0) : '',
  );
  const [rows, setRows] = useState<YearlyRow[]>(() => initialRows(initial));
  const [weekendRule, setWeekendRule] = useState<WeekendRule>(initial?.weekendRule ?? 'before');
  const [startDate, setStartDate] = useState<LocalDate>(initial?.startDate ?? today);
  const [endDate, setEndDate] = useState<LocalDate | null>(initial?.endDate ?? null);
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(initial?.accountId ?? null);
  const [tie, setTie] = useState<string>(
    s0?.kind === 'yearly_dates' && s0.withPayday
      ? `${s0.withPayday.sourceId}|${s0.withPayday.slot}`
      : '',
  );
  const tieOptions = salaries.flatMap((src) =>
    src.schedule.kind === 'semi_monthly'
      ? src.schedule.days.map((d, slot) => ({
          value: `${src.id}|${slot}`,
          label: copy.arrivesWithOption(src.name, dayOfMonthLabel(d)),
        }))
      : src.schedule.kind === 'monthly'
        ? [
            {
              value: `${src.id}|0`,
              label: copy.arrivesWithOption(src.name, dayOfMonthLabel(src.schedule.day)),
            },
          ]
        : [],
  );

  const updateRow = (i: number, patch: Partial<YearlyRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  function build(): IncomeSourceInput | null {
    let schedule: IncomeSchedule;
    let amounts: (number | null)[];
    switch (schedKind) {
      case 'semi_monthly':
        schedule = { kind: 'semi_monthly', days: [toInt(day1), toInt(day2)] };
        amounts = [parsePesoInput(amount1), parsePesoInput(amount2)];
        break;
      case 'monthly':
        schedule = { kind: 'monthly', day: toInt(monthDay) };
        amounts = [parsePesoInput(amount1)];
        break;
      case 'biweekly':
        schedule = { kind: 'biweekly', anchor };
        amounts = [parsePesoInput(amount1)];
        break;
      case 'weekly':
        schedule = { kind: 'weekly', anchor };
        amounts = [parsePesoInput(amount1)];
        break;
      case 'yearly_dates':
        schedule = {
          kind: 'yearly_dates',
          dates: rows.map((r) => `${pad(r.month)}-${pad(toInt(r.day))}`),
          ...(tie
            ? { withPayday: { sourceId: tie.split('|')[0]!, slot: Number(tie.split('|')[1]) } }
            : {}),
        };
        amounts = rows.map((r) => parsePesoInput(r.amount));
        break;
      case 'every_n_months':
        schedule = { kind: 'every_n_months', n: toInt(everyN), anchor };
        amounts = [parsePesoInput(amount1)];
        break;
      default:
        return null;
    }
    if (name.trim() === '' || amounts.some((a) => a === null)) return null;
    return {
      name: name.trim(),
      kind,
      schedule,
      defaultAmounts: amounts as number[],
      weekendRule,
      startDate,
      endDate,
      categoryId: initial?.categoryId ?? null,
      ...(accounts ? { accountId } : {}),
    };
  }

  const input = build();

  async function submit() {
    if (!input) return;
    setBusy(true);
    try {
      await onSubmit(input);
    } finally {
      setBusy(false);
    }
  }

  const amountField = (label: string) => (
    <TextField
      label={label}
      inputMode="decimal"
      value={amount1}
      onChange={(e) => setAmount1(e.target.value)}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <TextField
        label={copy.sourceName}
        placeholder={kind === 'salary' ? copy.sourceNamePlaceholder : '13th month pay'}
        value={name}
        maxLength={40}
        onChange={(e) => setName(e.target.value)}
      />
      <ChoiceChips<ScheduleKind>
        label={copy.howOften}
        value={schedKind}
        options={kind === 'salary' ? SALARY_KINDS : BONUS_KINDS}
        onChange={setSchedKind}
      />

      {schedKind === 'semi_monthly' ? (
        <>
          <TextField
            label={copy.firstPayday}
            inputMode="numeric"
            value={day1}
            onChange={(e) => setDay1(e.target.value)}
          />
          <TextField
            label={copy.secondPayday}
            inputMode="numeric"
            value={day2}
            onChange={(e) => setDay2(e.target.value)}
          />
          {amountField(copy.amountOn(dayOfMonthLabel(toInt(day1) || 15)))}
          <TextField
            label={copy.amountOn(dayOfMonthLabel(toInt(day2) || 31))}
            inputMode="decimal"
            value={amount2}
            onChange={(e) => setAmount2(e.target.value)}
          />
        </>
      ) : null}

      {schedKind === 'monthly' ? (
        <>
          <TextField
            label={copy.paydayDay}
            inputMode="numeric"
            value={monthDay}
            onChange={(e) => setMonthDay(e.target.value)}
          />
          {amountField(copy.amount)}
        </>
      ) : null}

      {schedKind === 'biweekly' || schedKind === 'weekly' ? (
        <>
          <TextField
            label={copy.recentPayday}
            type="date"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
          />
          {amountField(copy.amount)}
        </>
      ) : null}

      {schedKind === 'yearly_dates' ? (
        <div className="flex flex-col gap-3">
          {rows.map((r, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div className="flex gap-2">
                <div className="flex flex-1 flex-col gap-1">
                  <span className="text-sm font-medium text-muted" aria-hidden="true">
                    {copy.monthN(i)}
                  </span>
                  <select
                    aria-label={copy.monthN(i)}
                    className={INPUT}
                    value={r.month}
                    onChange={(e) => updateRow(i, { month: Number(e.target.value) })}
                  >
                    {MONTHS.map((m, mi) => (
                      <option key={m} value={mi + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <TextField
                  label={copy.dayN(i)}
                  inputMode="numeric"
                  value={r.day}
                  onChange={(e) => updateRow(i, { day: e.target.value })}
                />
              </div>
              <TextField
                label={copy.usualAmount(i)}
                inputMode="decimal"
                value={r.amount}
                onChange={(e) => updateRow(i, { amount: e.target.value })}
              />
              {rows.length > 1 ? (
                <Button
                  variant="ghost"
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                >
                  {copy.removeDate(i)}
                </Button>
              ) : null}
            </div>
          ))}
          {rows.length < 12 ? (
            <Button
              variant="secondary"
              onClick={() => setRows((rs) => [...rs, { month: 6, day: '30', amount: '' }])}
            >
              {copy.addDate}
            </Button>
          ) : null}
        </div>
      ) : null}

      {schedKind === 'yearly_dates' && tieOptions.length > 0 ? (
        <div className="flex flex-col gap-1">
          <label htmlFor="arrives-with" className="text-sm font-medium text-muted">
            {copy.arrivesWith}
          </label>
          <select
            id="arrives-with"
            className={INPUT}
            value={tie}
            onChange={(e) => setTie(e.target.value)}
          >
            <option value="">{copy.arrivesWithNone}</option>
            {tieOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {tie ? <p className="text-xs text-muted">{copy.arrivesWithHint}</p> : null}
        </div>
      ) : null}

      {schedKind === 'every_n_months' ? (
        <>
          <TextField
            label={copy.everyHowManyMonths}
            inputMode="numeric"
            value={everyN}
            onChange={(e) => setEveryN(e.target.value)}
          />
          <TextField
            label={copy.lastTimeItCame}
            type="date"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
          />
          {amountField(copy.amount)}
        </>
      ) : null}

      {accounts && accounts.length > 0 ? (
        <AccountChips
          label={copy.goesTo}
          accounts={accounts}
          value={accountId}
          onChange={setAccountId}
          hint={
            accounts.some((a) => a.id === accountId && !usedForSpending(a))
              ? copy.toSavingsNote
              : copy.receiveIntoSourceHint
          }
        />
      ) : null}
      <Segmented<WeekendRule>
        label={copy.weekendRule}
        value={weekendRule}
        options={[
          { value: 'before', label: copy.dayBefore },
          { value: 'after', label: copy.dayAfter },
        ]}
        onChange={setWeekendRule}
      />
      <TextField
        label={copy.trackFrom}
        type="date"
        value={startDate}
        onChange={(e) => e.target.value && setStartDate(e.target.value)}
      />
      <div className="flex flex-col gap-1">
        <TextField
          label={copy.lastPaydayBy}
          hint={copy.lastPaydayByHint}
          type="date"
          value={endDate ?? ''}
          min={startDate}
          onChange={(e) => setEndDate(e.target.value || null)}
        />
        {endDate ? (
          <Button variant="secondary" className="self-start" onClick={() => setEndDate(null)}>
            {copy.noEndDate}
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button variant="primary" block disabled={!input || busy} onClick={() => void submit()}>
        {copy.save}
      </Button>
    </div>
  );
}
